<script setup lang="ts">
import type { TemperatureUnit } from "@/stores/settings.store";
import { computed } from "vue";
import { Droplets } from "@lucide/vue";
import { useUnitConversion } from "@/composables/useUnitConversion";
import {
  rainComparison,
  rainPanelState,
  rainIntensity,
  validRain,
} from "@/utils/precipitationDisplay";

const props = defineProps<{
  wallMode?: boolean;
  unit: TemperatureUnit;
  rate?: number | null;
  hourly?: number | null;
  today?: number | null;
  storm?: number | null;
  last24?: number | null;
  forecast?: number | null;
}>();

const { convertPrecipitation, labels } = useUnitConversion(() => props.unit);
const state = computed(() => rainPanelState(props));
const intensity = computed(() => rainIntensity(props.rate));
const comparison = computed(() => rainComparison(props.today, props.forecast));

const statistics = computed(() => [
  { label: "Hourly", value: props.hourly },
  { label: "Storm", value: props.storm },
  { label: "24h", value: props.last24 },
]);

// With no usable forecast denominator, show presence, not a fictitious percent.
const todayFill = computed(() =>
  comparison.value.percent !== null
    ? comparison.value.fill
    : validRain(props.today) === null
      ? 0
      : props.today! > 0
        ? 100
        : 0,
);

const todayDescription = computed(
  () =>
    `${amount(props.today)} ${labels.value.precipitation} observed; ${amount(props.forecast)} ${labels.value.precipitation} forecast. ${comparison.value.exceeded ? "Above forecast." : ""}`,
);

/** Format a rainfall amount in the selected units, preserving missing values. */
function amount(value: number | null | undefined) {
  const converted = convertPrecipitation(validRain(value));
  return converted === null ? "—" : converted.toFixed(2);
}
</script>
<template>
  <section
    class="grid content-center"
    :class="
      wallMode
        ? 'grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] items-start gap-x-4 gap-y-[5px]'
        : 'grid-cols-2 gap-x-6 gap-y-[18px] min-[800px]:max-[1200px]:grid-cols-[auto_1fr_1fr_1.5fr] min-[800px]:max-[1200px]:items-center min-[800px]:max-[1200px]:gap-6'
    "
    aria-label="Rain measurements"
  >
    <template v-if="wallMode">
      <div
        :class="{
          'grid grid-cols-[auto_auto] items-baseline justify-between gap-1.5 [&_.rain-label]:mb-0 [&_strong]:text-2xl':
            validRain(storm) !== null,
        }"
      >
        <span
          class="rain-label block leading-[1.4] text-text-muted"
          :class="
            wallMode
              ? 'mb-1 text-[11px] font-semibold uppercase'
              : 'mb-[5px] text-[13px]'
          "
          >Rain today</span
        >
        <strong
          class="block text-[clamp(30px,3.1vw,42px)] leading-[1.1] font-[650] tracking-[-0.035em] whitespace-nowrap text-[var(--rain-text)] tabular-nums"
          >{{ amount(today) }}
          <small
            class="font-normal text-text-muted"
            :class="
              wallMode
                ? 'text-[13px] tracking-normal'
                : 'text-xs leading-normal'
            "
            >{{ labels.precipitation }}</small
          ></strong
        >
        <template v-if="validRain(storm) !== null">
          <span
            class="block text-[11px] leading-[1.4] font-semibold text-text-muted uppercase"
            >Storm total</span
          >
          <strong
            class="block text-2xl leading-[1.1] font-[650] tracking-[-0.035em] whitespace-nowrap text-[var(--rain-text)] tabular-nums"
            >{{ amount(storm) }}
            <small
              class="font-normal text-text-muted"
              :class="
                wallMode
                  ? 'text-[13px] tracking-normal'
                  : 'text-xs leading-normal'
              "
              >{{ labels.precipitation }}</small
            ></strong
          >
        </template>
      </div>
      <div class="min-w-0 border-l border-[var(--conditions-divider)] pl-4">
        <span
          class="rain-label block leading-[1.4] text-text-muted"
          :class="
            wallMode
              ? 'mb-1 text-[11px] font-semibold uppercase'
              : 'mb-[5px] text-[13px]'
          "
          >Rain rate</span
        >
        <strong
          class="block text-[clamp(30px,3.1vw,42px)] leading-[1.1] font-[650] tracking-[-0.035em] whitespace-nowrap text-[var(--rain-text)] tabular-nums"
          >{{ amount(rate) }}
          <small
            class="font-normal text-text-muted"
            :class="
              wallMode
                ? 'text-[13px] tracking-normal'
                : 'text-xs leading-normal'
            "
            >{{ labels.precipitation }}/hr</small
          ></strong
        >
        <div
          v-if="validRain(rate) !== null"
          class="w-full overflow-hidden rounded-full bg-[var(--rain-track)]"
          :class="wallMode ? 'mt-1.5 h-2' : 'h-1.5'"
          role="meter"
          aria-label="Rain intensity"
          :aria-valuenow="intensity.fill"
          :aria-valuemin="0"
          :aria-valuemax="100"
          :aria-valuetext="`${amount(rate)} ${labels.precipitation} per hour, ${intensity.label}`"
        >
          <span
            class="block h-full rounded-[inherit] bg-[linear-gradient(90deg,var(--rain-fill),var(--rain-fill-end))]"
            :style="{ width: intensity.fill + '%' }"
          />
        </div>
        <template v-else
          ><div
            class="w-full overflow-hidden rounded-full bg-[var(--rain-track)]"
            :class="wallMode ? 'mt-1.5 h-2' : 'h-1.5'"
            aria-hidden="true"
          />
          <span class="sr-only">Rate unavailable</span></template
        >
      </div>
      <p
        v-if="validRain(today) === null"
        class="col-span-full text-[10px] leading-[1.2] text-text-muted"
      >
        No station total<span v-if="validRain(forecast) !== null">
          · Forecast {{ amount(forecast) }} {{ labels.precipitation }}</span
        >
      </p>
    </template>
    <template v-else>
      <h3
        class="col-span-full flex items-center gap-2 text-lg leading-[1.2] font-semibold min-[800px]:max-[1200px]:col-auto [&_svg]:size-[22px] [&_svg]:stroke-[1.65] [&_svg]:text-[var(--rain-text)]"
      >
        <Droplets :size="26" aria-hidden="true" />Rain
      </h3>
      <div
        v-if="state !== 'measurements'"
        class="col-span-full text-[13px] leading-normal text-text-muted min-[800px]:max-[1200px]:col-[2/-1]"
      >
        <strong
          class="block text-[28px] leading-[1.2] font-[550] tabular-nums"
          :class="
            state === 'dry' ? 'text-[var(--rain-text)]' : 'text-text-primary'
          "
        >
          {{ state === "dry" ? amount(today) : "—" }}
          <small
            class="font-normal text-text-muted"
            :class="
              wallMode
                ? 'text-[13px] tracking-normal'
                : 'text-xs leading-normal'
            "
            v-if="state === 'dry'"
            >{{ labels.precipitation }}</small
          >
        </strong>
        <p class="mt-1.5">
          {{ state === "dry" ? "Recorded today" : "No station reading" }}
        </p>
        <p
          v-if="validRain(forecast) !== null"
          class="mt-3 text-xs leading-normal"
        >
          Today’s forecast: {{ amount(forecast) }} {{ labels.precipitation }}
        </p>
      </div>
      <template v-else>
        <div class="flex min-w-0 flex-col justify-between gap-2.5">
          <div>
            <span
              class="rain-label block leading-[1.4] text-text-muted"
              :class="
                wallMode
                  ? 'mb-1 text-[11px] font-semibold uppercase'
                  : 'mb-[5px] text-[13px]'
              "
              >Rate</span
            ><strong
              class="text-lg leading-[1.25] font-[550] whitespace-nowrap text-[var(--rain-text)] tabular-nums"
              >{{ amount(rate) }}
              <small
                class="font-normal text-text-muted"
                :class="
                  wallMode
                    ? 'text-[13px] tracking-normal'
                    : 'text-xs leading-normal'
                "
                >{{ labels.precipitation }}/hr</small
              ></strong
            >
          </div>
          <div
            class="w-full overflow-hidden rounded-full bg-[var(--rain-track)]"
            :class="wallMode ? 'mt-1.5 h-2' : 'h-1.5'"
            role="meter"
            aria-label="Rain intensity"
            :aria-valuenow="intensity.fill"
            :aria-valuemin="0"
            :aria-valuemax="100"
            :aria-valuetext="`${amount(rate)} ${labels.precipitation} per hour, ${intensity.label}`"
          >
            <span
              class="block h-full rounded-[inherit] bg-[linear-gradient(90deg,var(--rain-fill),var(--rain-fill-end))]"
              :style="{ width: intensity.fill + '%' }"
            />
          </div>
        </div>
        <div
          class="flex min-w-0 flex-col justify-between gap-2.5"
          :title="todayDescription"
        >
          <div>
            <span
              class="rain-label block leading-[1.4] text-text-muted"
              :class="
                wallMode
                  ? 'mb-1 text-[11px] font-semibold uppercase'
                  : 'mb-[5px] text-[13px]'
              "
              >Today</span
            ><strong
              class="text-lg leading-[1.25] font-[550] whitespace-nowrap text-[var(--rain-text)] tabular-nums"
              >{{ amount(today)
              }}<span
                v-if="comparison.forecast !== null && comparison.forecast > 0"
                class="text-[13px] font-normal text-text-muted"
              >
                / {{ amount(forecast) }}</span
              >
              <small
                class="font-normal text-text-muted"
                :class="
                  wallMode
                    ? 'text-[13px] tracking-normal'
                    : 'text-xs leading-normal'
                "
                >{{ labels.precipitation }}</small
              ></strong
            >
          </div>
          <div
            class="w-full overflow-hidden rounded-full bg-[var(--rain-track)]"
            :class="wallMode ? 'mt-1.5 h-2' : 'h-1.5'"
            role="img"
            :aria-label="todayDescription"
          >
            <span
              class="block h-full rounded-[inherit] bg-[linear-gradient(90deg,var(--rain-fill),var(--rain-fill-end))]"
              :style="{ width: todayFill + '%' }"
            />
          </div>
        </div>
        <dl
          class="col-span-full m-0 grid grid-cols-3 gap-4 min-[800px]:max-[1200px]:col-auto"
        >
          <div v-for="stat in statistics" :key="stat.label">
            <dt class="mb-[5px] text-xs leading-normal text-text-muted">
              {{ stat.label }}
            </dt>
            <dd
              class="m-0 text-base leading-[1.25] font-[550] whitespace-nowrap text-[var(--rain-text)] tabular-nums"
            >
              {{ amount(stat.value) }}
              <small
                class="font-normal text-text-muted"
                :class="
                  wallMode
                    ? 'text-[13px] tracking-normal'
                    : 'text-xs leading-normal'
                "
                >{{ labels.precipitation }}</small
              >
            </dd>
          </div>
        </dl>
      </template>
    </template>
  </section>
</template>
